import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "./Dashboard";
import { Th, Td } from "./Customers";
import { useAuth } from "@/contexts/AuthContext";

export default function Reports() {
  const { tenant } = useAuth();
  const [tab, setTab] = useState("aging");
  const [aging, setAging] = useState(null);
  const [scorecards, setScorecards] = useState([]);
  const [prs, setPRs] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [batches, setBatches] = useState([]);
  const [cycleCounts, setCycleCounts] = useState([]);
  const [rtv, setRTV] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/sales/aging").then(({ data }) => setAging(data)),
      api.get("/procurement/scorecards").then(({ data }) => setScorecards(data)),
      api.get("/procurement/prs").then(({ data }) => setPRs(data)),
      api.get("/inventory/transfers").then(({ data }) => setTransfers(data)),
      api.get("/inventory/batches").then(({ data }) => setBatches(data)),
      api.get("/inventory/cycle-counts").then(({ data }) => setCycleCounts(data)),
      api.get("/inventory/return-to-vendor").then(({ data }) => setRTV(data)),
    ]);
  }, []);

  const tabs = [
    { id: "aging", label: "AR Aging" },
    { id: "scorecards", label: "Supplier Scorecards" },
    { id: "prs", label: "Requisitions" },
    { id: "transfers", label: "Stock Transfers" },
    { id: "batches", label: "Batches & Expiry" },
    { id: "cycle", label: "Cycle Counts" },
    { id: "rtv", label: "Return to Vendor" },
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-12 max-w-[1400px]" data-testid="reports-page">
      <PageHeader title="Operations Reports" subtitle="AR Aging, Supplier Scorecards, PRs, Transfers, Batches, Cycle Counts, RTV — all required by the Gigmann blueprint." />

      <div className="mt-6 flex flex-wrap gap-1 border-b border-[#DEE2E6]">
        {tabs.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`reports-tab-${t.id}`}
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 ${tab === t.id ? "border-[#FF4F00] text-[#0A0A0A]" : "border-transparent text-[#495057] hover:text-[#0A0A0A]"}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {tab === "aging" && aging && (
          <div className="border border-[#0A0A0A] bg-white overflow-x-auto" data-testid="aging-report">
            <table className="w-full text-sm">
              <thead className="bg-[#0A0A0A] text-white text-[11px] uppercase tracking-[0.15em]">
                <tr><Th>Customer</Th><Th align="right">Current</Th><Th align="right">0–30</Th><Th align="right">31–60</Th><Th align="right">61–90</Th><Th align="right">90+</Th><Th align="right">Total</Th></tr>
              </thead>
              <tbody>
                {aging.customers.map((c, i) => (
                  <tr key={c.customer_id} data-testid={`aging-row-${i}`} className="border-b border-[#DEE2E6]">
                    <Td>{c.customer_name}</Td>
                    {["current","0-30","31-60","61-90","90+","total"].map((k) => (
                      <Td key={k} align="right" className={`tabular-nums ${k === "total" ? "font-bold" : ""} ${k === "90+" && c[k] > 0 ? "text-[#D90429]" : ""}`}>{c[k].toLocaleString()}</Td>
                    ))}
                  </tr>
                ))}
                {aging.customers.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-[#495057]">No outstanding receivables.</td></tr>}
              </tbody>
              <tfoot className="bg-[#F8F9FA]">
                <tr className="font-bold"><Td>Totals · {tenant?.currency}</Td>
                  {["current","0-30","31-60","61-90","90+"].map((k) => (
                    <Td key={k} align="right" className="tabular-nums">{aging.totals[k].toLocaleString()}</Td>
                  ))}
                  <Td align="right" className="tabular-nums text-[#FF4F00]">{Object.values(aging.totals).reduce((a,b)=>a+b,0).toLocaleString()}</Td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {tab === "scorecards" && (
          <div className="border border-[#0A0A0A] bg-white overflow-x-auto" data-testid="scorecards-report">
            <table className="w-full text-sm">
              <thead className="bg-[#0A0A0A] text-white text-[11px] uppercase tracking-[0.15em]">
                <tr><Th>Supplier</Th><Th>Rating</Th><Th align="right">Total spend</Th><Th align="right">Orders</Th><Th align="right">Clean match %</Th><Th align="right">Outstanding</Th></tr>
              </thead>
              <tbody>
                {scorecards.map((s, i) => (
                  <tr key={s.supplier_id} data-testid={`scorecard-row-${i}`} className="border-b border-[#DEE2E6]">
                    <Td><div className="font-medium">{s.supplier_name}</div></Td>
                    <Td>{"★".repeat(s.rating)}{"☆".repeat(5-s.rating)}</Td>
                    <Td align="right" className="tabular-nums font-bold">{tenant?.currency} {s.total_spend.toLocaleString()}</Td>
                    <Td align="right" className="tabular-nums">{s.orders_completed}/{s.total_orders}</Td>
                    <Td align="right" className="tabular-nums">{s.clean_match_rate == null ? "—" : `${s.clean_match_rate}%`}</Td>
                    <Td align="right" className="tabular-nums text-[#D90429]">{s.outstanding_payable.toLocaleString()}</Td>
                  </tr>
                ))}
                {scorecards.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-[#495057]">No suppliers yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {tab === "prs" && (
          <SimpleTable testid="prs-table" rows={prs} cols={[
            { k: "number", label: "PR #" }, { k: "department", label: "Dept" },
            { k: "justification", label: "Justification" }, { k: "status", label: "Status" },
            { k: "created_at", label: "Date", fmt: (v) => new Date(v).toLocaleDateString() },
          ]} actions={(row, reload) => row.status === "pending" ? (
            <div className="flex gap-1">
              <button onClick={async () => { await api.post(`/procurement/prs/${row.id}/approve`); reload(); }} className="px-2 py-1 text-xs bg-[#0B7A75] text-white" data-testid={`pr-approve-${row.id}`}>Approve</button>
              <button onClick={async () => { await api.post(`/procurement/prs/${row.id}/reject`); reload(); }} className="px-2 py-1 text-xs border border-[#D90429] text-[#D90429]">Reject</button>
            </div>
          ) : null}
          reload={() => api.get("/procurement/prs").then(({ data }) => setPRs(data))} />
        )}

        {tab === "transfers" && (
          <SimpleTable testid="transfers-table" rows={transfers} cols={[
            { k: "sku", label: "SKU" }, { k: "qty", label: "Qty" },
            { k: "from_warehouse_id", label: "From" }, { k: "to_warehouse_id", label: "To" },
            { k: "created_at", label: "Date", fmt: (v) => new Date(v).toLocaleString() },
          ]} />
        )}

        {tab === "batches" && (
          <SimpleTable testid="batches-table" rows={batches} cols={[
            { k: "sku", label: "SKU" }, { k: "batch_no", label: "Batch #" },
            { k: "qty_remaining", label: "Remaining" }, { k: "expiry_date", label: "Expiry" },
          ]} />
        )}

        {tab === "cycle" && (
          <SimpleTable testid="cycle-counts-table" rows={cycleCounts} cols={[
            { k: "id", label: "ID", fmt: (v) => v.slice(0,8) },
            { k: "total_variances", label: "Variances" },
            { k: "created_at", label: "Date", fmt: (v) => new Date(v).toLocaleString() },
          ]} />
        )}

        {tab === "rtv" && (
          <SimpleTable testid="rtv-table" rows={rtv} cols={[
            { k: "po_number", label: "PO" }, { k: "sku", label: "SKU" },
            { k: "qty", label: "Qty" }, { k: "credit_amount", label: "Credit" },
            { k: "reason", label: "Reason" },
            { k: "created_at", label: "Date", fmt: (v) => new Date(v).toLocaleString() },
          ]} />
        )}
      </div>
    </div>
  );
}

function SimpleTable({ rows, cols, actions, reload, testid }) {
  return (
    <div className="border border-[#0A0A0A] bg-white overflow-x-auto" data-testid={testid}>
      <table className="w-full text-sm">
        <thead className="bg-[#0A0A0A] text-white text-[11px] uppercase tracking-[0.15em]">
          <tr>{cols.map((c) => <Th key={c.k}>{c.label}</Th>)}{actions && <Th></Th>}</tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={row.id || i} data-testid={`${testid}-row-${i}`} className="border-b border-[#DEE2E6] hover:bg-[#F8F9FA]">
              {cols.map((c) => <Td key={c.k} className={typeof row[c.k] === "number" ? "tabular-nums" : ""}>{c.fmt ? c.fmt(row[c.k]) : (row[c.k] ?? "—")}</Td>)}
              {actions && <Td>{actions(row, reload)}</Td>}
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={cols.length + (actions ? 1 : 0)} className="p-8 text-center text-[#495057]">No rows.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
